import { flattenProjectScheduleMilestones } from "@/containers/Project/helper";
import { useMobileLayout } from "@/hooks/useAppMedia";
import { useProjectContext } from "@/providers";
import { useUpdateProjectMilestoneMutation } from "@/services";
import * as S from "@/sharedStyled";
import { Form, Input } from "antd";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";

const EditProjectMilestoneModal = () => {
  const intl = useIntl();

  const { projectId } = useParams();
  const { editMilestone, setEditMilestone, project, setProject } =
    useProjectContext();
  const isMobileLayout = useMobileLayout();
  const [formRef] = Form.useForm();
  const [selectedMilestone, setSelectedMilestone] = useState(null);
  const [existingMilestoneNames, setExistingMilestoneNames] = useState(null);
  const milestoneLoaded = editMilestone && selectedMilestone;

  const [updateProjectMilestone, { isLoading: updatingProjectMilestone }] =
    useUpdateProjectMilestoneMutation();

  useEffect(() => {
    if (!editMilestone || !project.schedules?.length) return;

    const flattenedMilestones = flattenProjectScheduleMilestones(project);
    const milestone = flattenedMilestones.find(
      (flatMilestone) => flatMilestone.id === editMilestone
    );
    const existingMilestoneNames = flattenedMilestones
      .filter((flatMilestone) => flatMilestone.id !== editMilestone)
      .map((flatMilestone) => ({ Name: flatMilestone.name.toLowerCase() }));
    setSelectedMilestone({
      ...milestone,
      displayName: milestone?.displayName || milestone?.name,
    });
    setExistingMilestoneNames(existingMilestoneNames);
  }, [editMilestone, formRef, project]);

  useEffect(() => {
    if (
      milestoneLoaded &&
      formRef.getFieldValue("displayName") !== selectedMilestone?.displayName
    ) {
      formRef.setFieldsValue({ displayName: selectedMilestone?.displayName });
    }
  }, [formRef, milestoneLoaded, selectedMilestone?.displayName]);

  const handleSave = () => {
    formRef.submit();
  };

  const handleSubmit = (data) => {
    const milestone = {
      ...selectedMilestone,
      displayName: data.displayName,
    };
    updateProjectMilestone({ projectId, milestone })
      .unwrap()
      .then((project) => {
        setProject(project);
        handleCancel();
      });
  };

  const handleCancel = () => {
    setEditMilestone(null);
    setSelectedMilestone(null);
    setExistingMilestoneNames(null);
  };

  if (!milestoneLoaded) {
    return <Form form={formRef} component={false} />;
  }

  return (
    <S.ResponsiveModal
      $mobile={isMobileLayout}
      maskClosable={false}
      title={intl.formatMessage({
        id: "editMilestonesModal.title",
      })}
      onOk={handleSave}
      onCancel={handleCancel}
      okButtonProps={{
        loading: updatingProjectMilestone,
        disabled: updatingProjectMilestone,
      }}
      open={!!editMilestone}
      okText={intl.formatMessage({
        id: "general.save",
      })}
      destroyOnHidden
    >
      <Form
        name="edit-notification-group"
        onFinish={handleSubmit}
        autoComplete="off"
        form={formRef}
        initialValues={selectedMilestone}
      >
        <Form.Item
          label={intl.formatMessage({ id: `editMilestonesModal.name` })}
          name="displayName"
          rules={[
            {
              transform: (value) => (value || "").trim(),
              required: true,
              whitespace: true,
              type: "string",
              message: "Name is required!",
            },
            {
              transform: (value) => (value || "").trim().toLowerCase(),
              validator: (_, value) => {
                if (existingMilestoneNames.some((x) => x.name === value)) {
                  return Promise.reject(new Error(`'${value}' already exist!`));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Input maxLength={250} />
        </Form.Item>
      </Form>
    </S.ResponsiveModal>
  );
};

export { EditProjectMilestoneModal };
